import React, { createContext, useCallback, useState, useContext } from 'react';
import Swal from 'sweetalert2';
import api from '../services/api';

// As interfaces User e AuthState permanecem as mesmas
export interface User {
  id: number;
  name: string;
  email: string;
  // ...outros campos do seu usuário
}

interface AuthState {
  token: string;
  user: User;
}

// 🔑 MUDANÇA 1: Interface de credenciais atualizada
interface SignInCredentials {
  email: string;
  password: string;
}

interface AuthContextData {
  user: User;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
  updateUser(user: User): void;
}

export const AuthContext = createContext<AuthContextData>(
  {} as AuthContextData
);

export const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@App:token'); // <-- Recomendo um nome mais genérico
    const user = localStorage.getItem('@App:user');

    if (token && user) {
      // Adiciona o token aos headers de todas as requisições futuras
      api.defaults.headers.authorization = `Bearer ${token}`;
      return { token, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });

  // 🚀 MUDANÇA 2: Função signIn reescrita para o backend Sanctum
  const signIn = useCallback(async ({ email, password }: SignInCredentials) => {
    try {
      // A resposta do nosso backend tem 'access_token'
      const response = await api.post('api/login', {
        email,
        password,
      });

      const { access_token, user } = response.data;

      localStorage.setItem('@App:token', access_token);
      localStorage.setItem('@App:user', JSON.stringify(user));

      // Define o header de autorização para as próximas requisições
      api.defaults.headers.authorization = `Bearer ${access_token}`;

      setData({ token: access_token, user });
    } catch (error) {
      Swal.fire('Falha no login', 'Verifique suas credenciais.', 'error');
      // Lança o erro para que a página de login possa tratar (ex: parar um loading)
      throw new Error('Login failed');
    }
  }, []);

  // 🚪 MUDANÇA 3: Função signOut implementada para chamar a API
  const signOut = useCallback(async () => {
    try {
      // Chama a API para invalidar o token no backend
      await api.post('api/logout');
    } catch (error) {
      // console.error(
      //   'API de logout falhou, mas o logout local prosseguirá.',
      //   error
      // );
    } finally {
      // Limpa os dados locais independentemente do sucesso da chamada da API
      localStorage.removeItem('@App:token');
      localStorage.removeItem('@App:user');

      // Remove o header de autorização
      delete api.defaults.headers.authorization;

      setData({} as AuthState);
    }
  }, []);

  // A função updateUser está correta, apenas ajustei a chave do localStorage
  const updateUser = useCallback(
    (user: User) => {
      localStorage.setItem('@App:user', JSON.stringify(user));
      setData({
        token: data.token,
        user,
      });
    },
    [data.token]
  );

  return (
    <AuthContext.Provider
      value={{ user: data.user, signIn, signOut, updateUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// O hook useAuth continua o mesmo
export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
